import { Tooltip, Box } from '@material-ui/core';
import { TextColor } from '@setvi/shared/enums';
import { QuoteProductDTO } from 'pages/quotes/types';
import { useState, useEffect, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';

interface ProfitMarginPercentageInputProps {
  value: number;
  productId: string;
  product: QuoteProductDTO;
  isEditable: boolean;
  onProfitMarginPercentageChange: (
    productId: string,
    product: QuoteProductDTO,
    value: string
  ) => void;
  maxValue?: number;
  minValue?: number;
  inputClassName?: string;

  prefix?: string;
  suppressSuffix?: boolean;
  textWithColors?: boolean;
  helperText?: JSX.Element;
  onFocusChange?: (isFocused: boolean) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  oldStyle?: boolean;
}

export const ProfitMarginPercentageInput = ({
  value,
  productId,
  product,
  isEditable,
  maxValue = 99.99,
  minValue = 0,
  onProfitMarginPercentageChange,
  inputClassName,
  prefix = '',
  suppressSuffix = false,
  textWithColors = false,
  onFocusChange,
  onBlur,
  onKeyDown,
  helperText,
  oldStyle
}: ProfitMarginPercentageInputProps) => {
  const limitedMaxValue = maxValue > 99.99 ? 99.99 : maxValue;

  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);

  // Check if the calculated value is out of bounds
  const outOfBoundsError = useMemo(() => {
    if (value < minValue) {
      return `Min value for this input is ${minValue}%`;
    }
    if (value > limitedMaxValue) {
      return `Max value for this input is ${limitedMaxValue}%`;
    }
    return null;
  }, [value, minValue, limitedMaxValue]);

  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Update displayValue when value prop changes
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value);
    }
  }, [value, isFocused]);

  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4
      }}>
      <Tooltip
        open={!!outOfBoundsError && tooltipVisible}
        title={outOfBoundsError || ''}
        arrow
        placement="top">
        <span
          onMouseEnter={() => setTooltipVisible(true)}
          onMouseLeave={() => setTooltipVisible(false)}>
          <NumericFormat
            value={displayValue}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            decimalSeparator="."
            suffix={suppressSuffix ? '' : ' %'}
            prefix={prefix}
            onFocus={() => {
              setIsFocused(true);
              setTooltipVisible(true);
              onFocusChange?.(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              setTooltipVisible(false);
              onFocusChange?.(false);
              onBlur?.();
            }}
            onKeyDown={onKeyDown}
            isAllowed={values => {
              const { floatValue } = values;

              // Allow empty/undefined values (user clearing input)
              if (floatValue === undefined) {
                return true;
              }

              // Only restrict range when user is focused (manually typing)
              // Allow any value when it comes from calculations (not focused)
              if (isFocused) {
                if (floatValue < minValue || floatValue > limitedMaxValue) {
                  return false;
                }
              }

              return true;
            }}
            onValueChange={values => {
              const { floatValue } = values;

              // If empty/undefined, set to 0
              const valueToSave = floatValue === undefined ? 0 : floatValue;

              setDisplayValue(valueToSave);

              // Only call parent handler when user is focused (actively editing)
              // This prevents loops when displayValue is updated from props
              if (isFocused) {
                onProfitMarginPercentageChange(
                  productId,
                  product,
                  valueToSave.toFixed(2)
                );
              }
            }}
            disabled={!isEditable}
            className={inputClassName}
            style={{
              fontWeight: oldStyle ? 400 : 600,
              // eslint-disable-next-line no-nested-ternary
              color: textWithColors
                ? value > 0
                  ? TextColor.Green
                  : TextColor.Red
                : 'inherit',
              ...(outOfBoundsError && {
                border: '1px solid #f44336',
                borderRadius: 4
              })
            }}
          />
        </span>
      </Tooltip>
      {!!helperText && helperText}
    </Box>
  );
};
